import { Dispatch, lazy, SetStateAction, useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import useAddPostForm, { PostFormData } from 'pages/addPost/hooks/useAddPostForm';
import PostDetailsForm from './PostDetailsForm';
import BrowseTemplatesModal from './BrowseTemplatesModal';
import {
  ArticleFieldsType,
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from 'pages/knowledge/hooks/useArticlesQuery';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { FormProvider } from 'react-hook-form';
import { ProjectRecommendationsType } from 'pages/projects/hooks/useProjectsQuery';
import AlertModal from 'components/utils/AlertModal';
import { FeedFilter } from 'api/KnowledgeAPI';
import {
  ArticleCreationMethods,
  ArticleCreationActions,
  DEFAULT_TEXT_FIELD,
  FULL_WIDTH_ARTICLE_FIELDS,
  useWriteWithAI,
  WriteWithAIForm,
  ParseFileForm,
  WeWillWriteForm,
} from '@buildwise/libs/posts';
import PostFormDialogActions from './PostFormDialogActions';
import PostFormDialogTitle from './PostFormDialogTitle';
import AskPeersForkSkeleton from '@buildwise/libs/posts/components/AskPeers/AskPeersFormSkeleton';
import SuggestionsFormSkeleton from '@buildwise/libs/posts/components/Suggestions/SuggestionsFormSkeleton';
import { theme } from 'config/theme';
import RefreshLineIcon from 'remixicon-react/RefreshLineIcon';
import useFetchEventSource from '@buildwise/pages/search/hooks/useFetchEventSource';
import { PYTHON_SERVICE_ENDPOINTS, PYTHON_SERVICE_URL } from '@buildwise/libs/services';
import useUserData from 'hooks/useUserData';

const WriteWithAIModal = lazy(
  () => import('@buildwise/libs/posts/components/WriteWithAI/WriteWithAIModal'),
);

export const $Dialog = styled(Dialog, { label: 'ActionModal' })(() => ({
  '& .MuiDialog-paper': {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0px 1px 0px #D9DBE9',
    transition: 'width 0.3s',
    maxWidth: 1280,
    width: 620,
    overflow: 'hidden',
  },
  '&.full-view': {
    '& .MuiBackdrop-root': {
      // background: theme.palette.custom.background,
    },
    '& .MuiDialog-paper': {
      transition: 'width 0.5s',
      width: '100%',
    },
  },
}));

export const $DialogTitle = styled(MuiDialogTitle, { label: 'DialogTitle' })(({ theme }) => ({
  margin: 0,
  padding: '0 24px',
  height: 72,
  lineHeight: '76px',
  color: '#000000',
  fontWeight: 700,
  fontSize: 32,
  '& .MuiIconButton-root:not(.resize)': {
    top: 16,
    right: 12,
    color: theme.palette.custom.lightBlack,
  },
}));

export const $DialogContent = styled(DialogContent, { label: 'DialogContent' })<{ width?: number }>(
  ({ theme, width }) => ({
    transition: 'width 0.3s, padding 0.3s',
    borderTop: `1px solid ${theme.palette.custom.line}`,
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    width: width || '100%',
    '&.full-view': {
      padding: 0,
      overflow: 'auto',
      height: 'calc(100vh - 205px)',
    },
  }),
);

type AddPostModalProps = {
  article?: ArticleType;
  postCreationMethod?: ArticleCreationMethods;
  articleId?: string;
  articleFormState?: PostFormData;
  articleType?: ArticleSourceType;
  createPlaylist?: boolean;
  defaultPlaylists?: { label: string; value: string }[];
  defaultProjectId?: string;
  formIds?: string[];
  forward?: boolean;
  isCopyPost?: boolean;
  isCreateDraft?: boolean;
  isEditing?: boolean;
  open: boolean;
  project?: ProjectRecommendationsType;
  publishArticleToCompanyIds?: string[];
  stakeholderId?: string;
  selectedFeedFilters?: FeedFilter[];
  defaultPrompt?: string;
  onAddArticleCallback?: (options: { article: ArticleType }) => void;
  onSaveCallback?: (article: ArticleType) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  updateArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
};

function AddPostModal({
  article: articleInfo = undefined,
  postCreationMethod = ArticleCreationMethods.WRITE_MANUALLY,
  articleId,
  articleFormState,
  articleType = ArticleSourceType.POST,
  createPlaylist = false,
  defaultPlaylists = [],
  defaultProjectId = '',
  formIds = [],
  forward = false,
  isCopyPost = false,
  isCreateDraft = false,
  isEditing = false,
  open,
  project,
  publishArticleToCompanyIds = [],
  stakeholderId,
  selectedFeedFilters,
  defaultPrompt = '',
  onAddArticleCallback,
  onSaveCallback,
  setOpen,
  updateArticleCallback,
}: AddPostModalProps) {
  const {
    article,
    articleCreationMethod,
    canPublishPost,
    discardLoading,
    isCreatingOrEditingPlaylist,
    isDragActive,
    isFullScreen,
    isLoadingArticleConfigFields,
    isPublishingFileAsOnePost,
    isSavingDraft,
    isWritingWithAI,
    loadingFiles,
    openConfirmation,
    openTemplates,
    postForm,
    selectedTemplate,
    articleFields,
    articleFormConfigName,
    startAnotherPost,
    closeModal,
    getRootProps,
    handleClose,
    handleTemplateSelection,
    onDrop,
    onForward,
    onSaveDraft,
    onSubmit,
    setArticleCreationMethod,
    setFullScreen,
    setOpenConfirmation,
    setOpenTemplates,
    setPublishingFileAsOnePost,
    setStartAnotherPost,
  } = useAddPostForm({
    articleFormState,
    articleId,
    articleType,
    articleFieldsType: articleType as unknown as ArticleFieldsType,
    createPlaylist,
    data: articleInfo,
    defaultPlaylists,
    defaultProjectId,
    formIds,
    forward,
    isCopyPost,
    isEditing,
    postCreationMethod,
    project,
    publishArticleToCompanyIds,
    stakeholderId,
    selectedFeedFilters,
    onAddArticleCallback,
    onSaveCallback,
    setOpen,
    updateArticleCallback,
  });

  const {
    tab,
    formState: writeWithAIFormState,
    openWriteWithAIModal,
    closeModal: closeWriteWithAIModal,
    onGenerate,
    onSelect,
    onRetry,
  } = useWriteWithAI({
    defaultPrompt,
    onGeneratePost: ({ title, description, attachments }) => {
      postForm.setValue('isWritingWithAI', true);
      postForm.setValue('title', title, {
        shouldDirty: true,
        shouldValidate: true,
      });
      postForm.setValue('description', description, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (attachments?.length) {
        postForm.setValue('attachments', [...attachments], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
  });
  const { fetchSSE, response, setResponse, reset: resetLlmFileSummary } = useFetchEventSource();
  const { companyId } = useUserData();
  const [isOnePostSelected, setOnePostSelected] = useState(false);
  const [downloadedFiles, setDownloadedFiles] = useState<AttachmentType[]>([]);

  const onRetryWriteWithAI = useCallback(() => {
    postForm.reset();
    writeWithAIFormState.reset({
      files: [],
      text: [
        { ...DEFAULT_TEXT_FIELD, text: writeWithAIFormState.getValues()?.text?.[0]?.text || '' },
      ],
      url: '',
    });
  }, [writeWithAIFormState, postForm]);

  const onParseFile = useCallback(
    async ({ isOnePostSelected }: { isOnePostSelected: boolean }) => {
      setOnePostSelected(isOnePostSelected);
      const { attachments = [] } = postForm.getValues();
      try {
        const formBody = new FormData();
        formBody.append('file', attachments[0]?.file);
        const _url = `${PYTHON_SERVICE_URL}${PYTHON_SERVICE_ENDPOINTS.GENERATE_ARTICLES_WITH_FILE}?company_id=${companyId}&create_single_article=${isOnePostSelected}`;

        fetchSSE(_url, formBody, true);
      } catch (error) {
        console.log('PARSE_FILE_ERROR', error);
      }
    },
    [companyId, postForm],
  );

  const resetAllForms = useCallback(() => {
    postForm.reset();
    writeWithAIFormState.reset();
    setOnePostSelected(false);
    setPublishingFileAsOnePost(false);
    resetLlmFileSummary();
  }, [postForm, writeWithAIFormState]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={
        articleType === ArticleSourceType.POST || articleType === ArticleSourceType.LESSON
          ? 'md'
          : 'sm'
      }
      fullScreen={isFullScreen}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      disableEnforceFocus={true} // for enabling insert link modal in tinymce editor
      disableAutoFocus={true}
      TransitionComponent={GrowDialogTransition}
      sx={{
        WebkitOverflowScrolling: 'touch',
        '& .MuiPaper-root': {
          minHeight: '60%',
          transition: 'all 0.2s',
          ...((articleType === ArticleSourceType.POST ||
            articleType === ArticleSourceType.LESSON) && {
            height: isFullScreen ? '100%' : '90%',
          }),
        },
      }}
    >
      <FormProvider {...postForm}>
        <PostFormDialogTitle
          article={article}
          articleType={articleType}
          createPlaylist={createPlaylist}
          forward={forward}
          isEditing={isEditing}
          isFullScreen={isFullScreen}
          handleClose={handleClose}
          setFullScreen={setFullScreen}
          setOpenTemplates={setOpenTemplates}
        />
        <DialogContent
          id="dialog-description"
          sx={{ p: 1, py: 0 }}
          onPaste={(event) => onDrop(Array.from(event?.clipboardData?.files || []))}
          dividers
        >
          <Box maxWidth={theme.maxWidth} margin="0 auto" width="100%">
            {/* <StickyContainer>
          <Collapse in={isFormError}>
            <Box sx={{ padding: '16px 0 8px' }}>
              <Alert
                icon={<ErrorWarningLineIcon color={theme.palette.custom.mangoTango} />}
                sx={(theme) => ({
                  backgroundColor: theme.palette.custom.oldLace,
                  color: theme.palette.custom.nutmegWood,
                })}
              >
                Please add a title to the post.
              </Alert>
            </Box>
          </Collapse>
        </StickyContainer> */}
            {!isEditing &&
              !forward &&
              !createPlaylist &&
              articleType === ArticleSourceType.POST &&
              !defaultPrompt && (
                <ArticleCreationActions
                  articleCreationMethod={articleCreationMethod}
                  resetAllForms={resetAllForms}
                  setArticleCreationMethod={setArticleCreationMethod}
                />
              )}
            {articleCreationMethod === ArticleCreationMethods.WRITE_WITH_AI &&
              isWritingWithAI &&
              !defaultPrompt && (
                <Box display="flex" alignItems="center" justifyContent="flex-end" px={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshLineIcon size={18} />}
                    onClick={onRetryWriteWithAI}
                  >
                    Retry
                  </Button>
                </Box>
              )}
            {(articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY ||
              articleCreationMethod === ArticleCreationMethods.PARSE_FILE ||
              articleCreationMethod === ArticleCreationMethods.WE_WILL_WRITE ||
              isWritingWithAI) && (
              <UploadFilesContainer
                {...getRootProps()}
                aria-disabled={postForm.formState.isSubmitting}
                onFocus={(event) => event.preventDefault()}
                onBlur={(event) => event.preventDefault()}
                sx={(theme) => ({
                  border: isDragActive
                    ? `2px dashed ${theme.palette.custom.lightBlack}`
                    : '2px dashed transparent',
                  opacity: isDragActive ? 0.6 : 1,
                  transition: '0.4s',
                  ...(isDragActive && {
                    borderColor: theme.palette.custom.link,
                    backgroundColor: theme.palette.custom.mariner01,
                    '& th': {
                      backgroundColor: theme.palette.custom.mariner01,
                    },
                  }),
                })}
              >
                {articleType === ArticleSourceType.QUESTION && (
                  <Note>
                    Ask for input from peers who probably did a lot of the same things as you are.
                  </Note>
                )}
                {articleType === ArticleSourceType.SUGGESTION && (
                  <Note>
                    Submit feedback to design or other downstream teams to prevent recurring issues.
                  </Note>
                )}
                {(articleCreationMethod === ArticleCreationMethods.WRITE_MANUALLY ||
                  isWritingWithAI ||
                  isPublishingFileAsOnePost) &&
                  articleCreationMethod !== ArticleCreationMethods.WE_WILL_WRITE &&
                  (articleCreationMethod !== ArticleCreationMethods.PARSE_FILE ||
                    (articleCreationMethod === ArticleCreationMethods.PARSE_FILE &&
                      isPublishingFileAsOnePost)) && (
                    <PostDetailsForm
                      article={article}
                      articleCreationMethod={articleCreationMethod}
                      articleType={articleType}
                      skeleton={
                        articleType === ArticleSourceType.QUESTION ? (
                          <AskPeersForkSkeleton />
                        ) : articleType === ArticleSourceType.SUGGESTION ? (
                          <SuggestionsFormSkeleton />
                        ) : undefined
                      }
                      getFieldWidth={({ field }) => {
                        return articleType === ArticleSourceType.QUESTION ||
                          articleType === ArticleSourceType.SUGGESTION
                          ? FULL_WIDTH_ARTICLE_FIELDS.includes(field.entryCode) || !isFullScreen
                            ? '100%'
                            : '50%'
                          : undefined;
                      }}
                      configName={articleFormConfigName}
                      forward={forward}
                      selectedTemplateId={selectedTemplate?.id || ''}
                      isFullScreen={isFullScreen}
                      isEditing={isEditing}
                      onDrop={onDrop}
                      loadingFiles={loadingFiles}
                      articleFields={articleFields}
                      isCreatingOrEditingPlaylist={isCreatingOrEditingPlaylist}
                    />
                  )}
                {articleCreationMethod === ArticleCreationMethods.PARSE_FILE &&
                  !isPublishingFileAsOnePost && (
                    <ParseFileForm
                      article={article}
                      articleType={articleType}
                      isFullScreen={isFullScreen}
                      loadingFiles={loadingFiles}
                      onDrop={onDrop}
                      response={response}
                      downloadedFiles={downloadedFiles}
                      setDownloadedFiles={setDownloadedFiles}
                    />
                  )}
                {articleCreationMethod === ArticleCreationMethods.WE_WILL_WRITE && (
                  <WeWillWriteForm
                    article={article}
                    articleType={articleType}
                    isFullScreen={isFullScreen}
                    loadingFiles={loadingFiles}
                    onDrop={onDrop}
                    response={response}
                    downloadedFiles={downloadedFiles}
                    setDownloadedFiles={setDownloadedFiles}
                  />
                )}
              </UploadFilesContainer>
            )}
            {articleCreationMethod === ArticleCreationMethods.WRITE_WITH_AI && !isWritingWithAI && (
              <Box p={2}>
                <FormProvider {...writeWithAIFormState}>
                  <WriteWithAIForm />
                </FormProvider>
              </Box>
            )}
          </Box>
        </DialogContent>
        {!isLoadingArticleConfigFields && (
          <DialogActions
            sx={(theme) => ({ maxWidth: theme.maxWidth, margin: '0 auto', width: '100%' })}
          >
            <PostFormDialogActions
              article={article}
              articleType={articleType}
              canPublishPost={canPublishPost}
              createPlaylist={createPlaylist}
              forward={forward}
              isCreateDraft={isCreateDraft}
              isEditing={isEditing}
              isSavingDraft={isSavingDraft}
              loadingFiles={loadingFiles}
              onForward={onForward}
              onSaveDraft={onSaveDraft}
              onSubmit={onSubmit}
              setStartAnotherPost={setStartAnotherPost}
              startAnotherPost={startAnotherPost}
              articleCreationMethod={articleCreationMethod}
              onGenerate={onGenerate}
              onSelect={onSelect}
              writeWithAIFormState={writeWithAIFormState}
              onParseFile={onParseFile}
              response={response}
              setResponse={setResponse}
              setOpen={setOpen}
              isOnePostSelected={isOnePostSelected}
              isPublishingFileAsOnePost={isPublishingFileAsOnePost}
              setPublishingFileAsOnePost={setPublishingFileAsOnePost}
              downloadedFiles={downloadedFiles}
            />
          </DialogActions>
        )}
      </FormProvider>

      {openTemplates && (
        <BrowseTemplatesModal
          open={openTemplates}
          setOpen={setOpenTemplates}
          onSelect={handleTemplateSelection}
          template={selectedTemplate}
          hasNewChanges={postForm.formState.isDirty}
        />
      )}

      {openConfirmation && (
        <AlertModal
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          handleCancel={() => {
            closeModal();
          }}
          onSubmit={isEditing ? onSubmit : onSaveDraft}
          loading={postForm.formState.isSubmitting}
          cancelLoading={discardLoading}
          title="Confirm"
          submitButtonName={isEditing ? 'Save Changes' : 'Save as Draft'}
          cancelButtonName="Discard"
          description={'Are you sure you want to discard your changes?'}
          discardActions
        />
      )}

      {openWriteWithAIModal && (
        <FormProvider {...writeWithAIFormState}>
          <WriteWithAIModal
            open={openWriteWithAIModal}
            tab={tab}
            closeModal={closeWriteWithAIModal}
            onGenerate={onGenerate}
            onSelect={onSelect}
            onRetry={onRetry}
          />
        </FormProvider>
      )}
    </Dialog>
  );
}

// const StickyContainer = styled(Box, { label: 'StickyContainer' })(({ theme }) => ({
//   position: 'sticky',
//   top: '0',
//   backgroundColor: theme.palette.custom.white,
//   zIndex: 99,
// }));

// const Note = styled(Typography, { label: 'Note' })({
//   fontSize: 16,
//   padding: '16px 16px 0',
// });

const Note = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.tuna,
  fontSize: 16,
  marginBottom: 24,
}));

export const UploadFilesContainer = styled(Box)(() => ({
  height: '100%',
  borderRadius: 4,
  transition: 'background-color 0.3s',
  border: '2px dashed transparent',
  padding: 16,
}));

export default AddPostModal;
